/**
 * Client d'analytique produit : mise en file, lots, envoi same-origin.
 *
 * Deux propriétés le définissent, et aucune n'est négociable :
 *
 *  1. **Rien n'est écrit sur le terminal.** Pas de cookie, pas de
 *     `localStorage`, pas de `sessionStorage`, pas d'empreinte navigateur.
 *     L'identifiant de session vit en mémoire ; l'identité durable est
 *     résolue côté serveur (compte JWT, ou empreinte salée du jour).
 *
 *     ⚠️ Cela ne place **pas** la collecte hors du champ de l'article 82,
 *     contrairement à ce que ce fichier a longtemps affirmé. L'article vise
 *     aussi l'**accès** à des informations stockées dans le terminal, et
 *     `currentContext()` en lit plusieurs (fuseau, langue, viewport, et via
 *     `detectPerfTier` le nombre de cœurs et la définition d'écran). Ce qui
 *     évite le bandeau est l'**exemption de mesure d'audience** : première
 *     partie, finalité statistique, aucun recoupement, aucune publicité,
 *     rétention bornée, opposition possible. Ces conditions sont des
 *     contraintes de conception, pas un état de fait : les tenir est le
 *     travail de ce module et de son relais.
 *  2. **Elle ne peut pas casser l'application.** Toute erreur d'envoi est
 *     avalée, la file est bornée, et rien n'attend le réseau.
 *
 * L'envoi passe par `/api/events` (même origine, donc invisible pour les
 * bloqueurs) avec `fetch({ keepalive: true })` plutôt que `sendBeacon` :
 * `sendBeacon` ne permet pas de poser d'en-tête, ce qui ferait perdre le JWT
 * — donc l'attribution au compte — précisément sur l'événement de fin de
 * session, qui est celui qui compte le plus.
 */
import { getToken } from "@/lib/api/auth";
import { detectPerfTier } from "@/lib/perf/detect-perf-tier";
import { type DeviceClass, detectDeviceClass } from "./device-class";
import type { VirtuosaEvent } from "./events";

const ENDPOINT = "/api/events";

/** Envoi périodique, même si le seuil n'est pas atteint. */
const FLUSH_INTERVAL_MS = 5_000;

/**
 * Au-delà, on n'attend pas le minuteur.
 *
 * Reste **strictement** sous le plafond serveur (`MAX_EVENTS_PER_BATCH = 50`) :
 * un lot plus gros est rejeté en bloc par la validation, donc entièrement
 * perdu. La marge couvre les événements qu'un seul geste peut ajouter entre le
 * franchissement du seuil et la construction du corps.
 *
 * Monté de 20 à 45 : à 20, une frappe soutenue dans la recherche du catalogue
 * déclenchait un envoi toutes les trois secondes et faisait sauter le limiteur
 * de débit du proxy same-origin. Le lot refusé était alors perdu pour de bon —
 * la file est purgée **avant** l'envoi — et c'était souvent celui qui portait
 * les événements de fin de session, qui n'ont pas de seconde chance.
 */
const FLUSH_THRESHOLD = 45;

/**
 * Garde-fou mémoire : si le réseau est coupé durablement, la file ne doit pas
 * grossir sans fin. Les plus anciens événements sont sacrifiés en premier.
 */
const MAX_QUEUE = 200;

/**
 * Préférence d'opposition. C'est la **seule** clé que l'analytique écrit sur
 * le terminal, et elle n'est écrite que si l'utilisateur exprime son refus :
 * mémoriser un refus pour l'honorer relève de la nécessité, pas du traçage.
 */
export const OPT_OUT_STORAGE_KEY = "virtuosa.analytics.optout";

interface QueuedEvent {
  name: string;
  occurredAt: number;
  /**
   * Le chemin **au moment de la mise en file**, et non à celui de l'envoi.
   *
   * Le contexte de lot ne porte qu'un chemin pour vingt événements, lu au
   * flush : les rapports de chemins décrivaient donc la page où l'on se
   * trouvait cinq secondes plus tard. Les événements de départ étaient les
   * plus mal servis — `practice_session_ended` et `studio_empty_dismissed`
   * sont émis au démontage, donc relus sous le chemin de la destination : à
   * « où les gens abandonnent », la mesure répondait « là où ils sont arrivés
   * ensuite ».
   *
   * `page_viewed` est le seul cas particulier, et ce n'est pas une préférence
   * de lecture : Next.js appelle `onRouterTransitionStart` **avant** que
   * l'URL ne change, donc `window.location` porte encore la page qu'on quitte.
   * Le chemin juste est celui que l'événement transporte lui-même.
   */
  pathname: string | null;
  properties: Record<string, unknown>;
}

/**
 * Ce qui décrit la **session** plutôt qu'un événement, envoyé une fois par lot.
 *
 * ⚠️ Miroir de `api/src/Api/Request/Analytics/IngestContextRequest.php` : un
 * champ ajouté ici et absent là-bas est silencieusement ignoré à l'ingestion
 * (le désérialiseur tolère les attributs inconnus). Les deux se modifient
 * ensemble, comme les deux listes d'événements.
 */
interface BatchContext {
  sessionId: string;
  pathname: string | null;
  appVersion: string | null;
  timezone: string | null;
  locale: string | null;
  perfTier: string | null;
  deviceClass: DeviceClass | null;
  viewportWidth: number | null;
  viewportHeight: number | null;
  consentScope: string | null;
}

let queue: QueuedEvent[] = [];
let timer: ReturnType<typeof setTimeout> | null = null;
let started = false;

/**
 * Identifiant de session, **en mémoire uniquement**. Il survit à la navigation
 * SPA (le Studio reste monté), pas à un rechargement — comportement voulu :
 * une session correspond à une visite continue, et rien ne persiste.
 */
let sessionId = "";

/**
 * Quand la session courante a commencé, et quand elle a vu son dernier
 * événement. Les deux bornes servent à la faire rouler.
 */
let sessionStartedAt = 0;
let sessionLastEventAt = 0;

/**
 * Une visite continue, au sens de PostHog : « A new session is started by
 * default when either there's no activity for 30 minutes or the session has
 * reached the maximum session duration of 24 hours. »
 *
 * Sans ce roulement, l'identifiant était tiré une seule fois, à l'évaluation du
 * module, et ne changeait plus. Or l'identifiant de session est un UUIDv7 dont
 * PostHog **vérifie l'horodatage** : « The timestamp part of the UUIDv7 plus 24
 * hours must be after the timestamp of the last event in the session. Any
 * events with session IDs that do not meet these requirements are not included
 * in session aggregations. »
 *
 * Sur un poste de bureau qu'on ferme le soir, le cas n'arrivait jamais. Sur la
 * tablette posée sur le pupitre — la posture de référence du produit, en
 * application empaquetée qui n'est jamais rechargée — c'était le cas nominal :
 * passé la première journée, **tout** sortait des agrégations de session, et
 * silencieusement.
 *
 * Le seuil est à 23 h et non 24 : la fenêtre se compte depuis l'horodatage
 * porté par l'identifiant, et un lot peut partir jusqu'à cinq secondes après
 * son dernier événement.
 */
const SESSION_MAX_MS = 23 * 60 * 60 * 1000;
const SESSION_IDLE_MS = 30 * 60 * 1000;

/**
 * Fait rouler la session si elle a dépassé une de ses deux bornes.
 *
 * Appelée à chaque mise en file, c'est-à-dire au seul moment où la question se
 * pose : une session qui ne produit rien n'a pas besoin d'être renouvelée.
 */
function rollSessionIfStale(now: number): void {
  if (sessionId === "") {
    return;
  }

  const tooOld = now - sessionStartedAt >= SESSION_MAX_MS;
  const tooQuiet = sessionLastEventAt !== 0 && now - sessionLastEventAt >= SESSION_IDLE_MS;

  if (tooOld || tooQuiet) {
    sessionId = newSessionId();
    sessionStartedAt = now;
  }

  sessionLastEventAt = now;
}

/** Version applicative courante, poussée par le contexte « Quoi de neuf ». */
let appVersion: string | null = null;

/**
 * L'opposition exprimée pendant cette session, indépendamment du stockage.
 *
 * Safari en navigation privée, un navigateur qui refuse les données de site,
 * un `localStorage` plein : l'écriture lève, et l'opposition était alors
 * **avalée en silence** pendant que l'interface affichait qu'elle avait été
 * prise en compte. Un refus qu'on n'honore pas parce qu'on n'a pas su
 * l'écrire est pire que pas de bouton du tout. La mémoire tient donc
 * l'opposition pour la session en cours ; le stockage n'ajoute que sa
 * persistance, et son échec se dit à l'appelant plutôt que de se taire.
 */
let optedOutInMemory: boolean | null = null;

export function isOptedOut(): boolean {
  if (optedOutInMemory !== null) {
    return optedOutInMemory;
  }

  try {
    // Le garde de typage est **dans** le `try` : sur un navigateur qui refuse
    // les données de site, ce n'est pas la résolution du nom qui échoue mais
    // l'accès à la propriété, et `typeof` ne rattrape que la première.
    if (typeof localStorage === "undefined") {
      return false;
    }

    return localStorage.getItem(OPT_OUT_STORAGE_KEY) === "1";
  } catch {
    // Sans preuve d'opposition, on reste sur le comportement par défaut.
    return false;
  }
}

/**
 * Enregistre l'opposition, et dit si elle survivra au rechargement.
 *
 * @returns `false` quand le stockage a refusé : le choix vaut pour la session,
 *          l'appelant doit le dire plutôt que de laisser croire l'inverse.
 */
export function setOptedOut(optedOut: boolean): boolean {
  optedOutInMemory = optedOut;

  if (optedOut) {
    // Ce qui est déjà en file n'a pas à partir : l'opposition est immédiate.
    queue = [];
  }

  try {
    if (optedOut) {
      localStorage.setItem(OPT_OUT_STORAGE_KEY, "1");
    } else {
      localStorage.removeItem(OPT_OUT_STORAGE_KEY);
    }

    return true;
  } catch {
    return false;
  }
}

/**
 * Renseigne la version applicative jointe à chaque lot. Appelé par le contexte
 * « Quoi de neuf », seul détenteur de l'identifiant de release courant.
 */
export function setAppVersion(version: string | null): void {
  appVersion = version;
}

/**
 * La version applicative courante, pour qui doit la joindre à autre chose que
 * la file d'événements — le formulaire de retour, qui passe par
 * `/api/feedback` et non par les lots.
 */
export function getAppVersion(): string | null {
  return appVersion;
}

function currentContext(): BatchContext {
  const hasWindow = typeof window !== "undefined";

  return {
    sessionId,
    pathname: hasWindow ? window.location.pathname : null,
    appVersion,
    timezone: resolveTimezone(),
    locale: typeof navigator !== "undefined" ? navigator.language : null,
    perfTier: hasWindow ? detectPerfTier() : null,
    // Propriété de session au même titre que le palier de perf, d'où sa place
    // ici plutôt que dans une page vue : elle segmente ainsi tout ce qui est
    // émis, et pas seulement l'événement qui l'aurait portée.
    deviceClass: detectDeviceClass(),
    viewportWidth: hasWindow ? Math.round(window.innerWidth) : null,
    viewportHeight: hasWindow ? Math.round(window.innerHeight) : null,
    // Réservé au futur opt-in ciblé (replay, capture MIDI détaillée). La
    // collecte de base n'en a pas besoin.
    consentScope: null,
  };
}

/**
 * Le fuseau tient lieu de signal géographique : il évite d'envoyer la moindre
 * IP à un outil tiers tout en gardant une segmentation exploitable.
 */
function resolveTimezone(): string | null {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone ?? null;
  } catch {
    return null;
  }
}

/**
 * Un identifiant aléatoire au format UUID, utilisable **partout**.
 *
 * `crypto.randomUUID` n'existe qu'en **contexte sécurisé** (HTTPS, ou
 * `localhost`). La build Capacitor charge l'interface depuis
 * `http://<IP du Mac>:4000`, donc en contexte non sécurisé : l'appel y lève
 * un `TypeError`. Comme `startAnalytics()` tourne à l'évaluation du module
 * `instrumentation-client.ts`, c'est-à-dire avant l'hydratation, cette seule
 * erreur suffisait à faire échouer tout le bundle client : l'interface rendue
 * par le serveur restait affichée, mais définitivement inerte. Ne jamais
 * réintroduire `randomUUID` ici, ni ailleurs sur un chemin de mesure.
 *
 * `crypto.getRandomValues`, lui, est disponible partout.
 *
 * # Pourquoi la **version 7** et pas la 4
 *
 * PostHog exige un UUIDv7 pour `$session_id` : « Any events with session IDs
 * that do not meet these requirements are not included in session
 * aggregations » (posthog.com/docs/data/sessions). Un v4 ne porte aucun
 * horodatage, donc aucune des deux contraintes de fenêtre ne peut être
 * vérifiée, et **tous** les événements sortaient des agrégations de session —
 * silencieusement : les insights par événement fonctionnaient, seules les vues
 * de session restaient vides. Les 48 bits de poids fort portent donc l'instant
 * de création, en big-endian, comme le veut la RFC 9562.
 *
 * Conséquence à connaître : un onglet laissé ouvert plus de 24 h dépasse la
 * fenêtre admise et ressort des agrégations. La session étant tirée au sort à
 * chaque chargement, le cas est rare, pas impossible.
 */
/**
 * Des octets aléatoires, quoi qu'il arrive.
 *
 * `crypto.getRandomValues` est disponible partout, mais « partout » est
 * exactement ce qu'on croyait de `crypto.randomUUID` avant qu'il ne fige tout
 * le bundle client sous Capacitor. Un identifiant de session n'a aucune
 * exigence cryptographique — il ne fait que distinguer deux visites — donc le
 * repli ne coûte rien, et il retire à la mesure son dernier moyen de casser
 * l'application.
 */
function randomBytes(length: number): Uint8Array {
  try {
    return crypto.getRandomValues(new Uint8Array(length));
  } catch {
    const bytes = new Uint8Array(length);

    for (let i = 0; i < length; i += 1) {
      bytes[i] = Math.floor(Math.random() * 256);
    }

    return bytes;
  }
}

export function newUuidV7(): string {
  const bytes = randomBytes(16);
  const millis = Date.now();

  // 48 bits d'horodatage, du plus significatif au moins significatif.
  for (let i = 0; i < 6; i += 1) {
    bytes[i] = Math.floor(millis / 2 ** (8 * (5 - i))) & 0xff;
  }

  // Version 7, variante RFC 9562.
  bytes[6] = (bytes[6] & 0x0f) | 0x70;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;

  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");

  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

/** Identifiant de la session courante. */
function newSessionId(): string {
  return newUuidV7();
}

/** Démarre le client. Idempotent : appelé depuis `instrumentation-client.ts`. */
export function startAnalytics(): void {
  if (started || typeof window === "undefined") {
    return;
  }

  started = true;
  sessionId = newSessionId();
  sessionStartedAt = Date.now();
  sessionLastEventAt = 0;

  // `visibilitychange` est le signal fiable sur mobile, où `pagehide` peut ne
  // jamais arriver (application mise en arrière-plan puis tuée par l'OS).
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") {
      flush();
    }
  });

  window.addEventListener("pagehide", () => {
    flush();
  });
}

/**
 * Ouvre une nouvelle session anonyme. À appeler à la déconnexion.
 *
 * La session est l'identité anonyme transmise à PostHog, et `logout()` ne
 * recharge pas la page : sans ce reset, la session survivrait au changement de
 * compte. Sur un poste partagé, la personne suivante se connecterait sous la
 * même identité anonyme, et les deux comptes finiraient fusionnés en une seule
 * personne — ce que PostHog ne sait pas défaire.
 *
 * La file est vidée **avant** de tourner la page, pour que les événements de
 * la session qui s'achève partent bien sous son identité, et tant que le jeton
 * de l'utilisateur sortant permet encore de les lui rattacher.
 */
export function resetAnalyticsSession(): void {
  if (typeof window === "undefined") {
    return;
  }

  flush();
  sessionId = newSessionId();
  sessionStartedAt = Date.now();
  sessionLastEventAt = 0;

  for (const listener of sessionResetListeners) {
    silently(listener);
  }
}

/**
 * Ce qui doit être oublié quand une nouvelle session anonyme s'ouvre.
 *
 * Tout état « une fois par session » vit ailleurs — le dédoublonnage des
 * jalons dans la barrière publique, l'amorçage des franchissements dans la
 * page de parcours — et rien ne le remettait à zéro : le contrat écrit disait
 * « pour la session courante », le comportement disait « pour la vie de
 * l'onglet ». Sur un poste partagé, la personne suivante héritait donc du
 * silence de la précédente.
 *
 * Le sens de la dépendance est imposé : `index.ts` importe déjà ce module, et
 * l'inverse créerait un cycle. C'est donc à l'état de venir s'inscrire ici.
 */
const sessionResetListeners = new Set<() => void>();

export function onAnalyticsSessionReset(listener: () => void): () => void {
  sessionResetListeners.add(listener);

  return () => {
    sessionResetListeners.delete(listener);
  };
}

/**
 * Le chemin à attribuer à un événement au moment où il est mis en file.
 *
 * Un événement qui porte lui-même un `pathname` fait foi : il sait de quelle
 * page il parle mieux que `window.location`. C'est indispensable pour les deux
 * événements de navigation, et pour des raisons **opposées** — `page_viewed`
 * nomme la destination alors que l'URL n'a pas encore changé, `page_left`
 * nomme la page qu'on quitte. Voir {@link QueuedEvent.pathname}.
 */
function pathnameOf(properties: Record<string, unknown>): string | null {
  if (typeof properties.pathname === "string") {
    return properties.pathname;
  }

  return typeof window === "undefined" ? null : window.location.pathname;
}

/**
 * Les deux portes du module, et le seul endroit où l'invariant « la mesure ne
 * peut pas casser l'application » se tient réellement.
 *
 * Le tenir ici plutôt qu'aux trente sites d'appel n'est pas une commodité :
 * c'est la seule version qui reste vraie quand quelqu'un ajoute le trente et
 * unième. Un `track()` nu dans le `.then()` d'une suppression de morceau
 * rejetait la promesse du produit et faisait réapparaître à l'écran une ligne
 * déjà supprimée en base ; un `endExerciseSession()` nu dans un effet React
 * faisait tomber toute la section sur sa frontière d'erreur. Dans les deux
 * cas, la cause était un `localStorage` qui lève — un navigateur qui refuse
 * les données de site, une WebView au stockage coupé — sur un chemin que
 * personne ne soupçonnait de toucher au stockage.
 *
 * Ce que la mesure perd en cas d'échec, c'est un lot. Ce que l'application
 * perdrait sans ce filet, c'est la confiance de quelqu'un.
 */
/** Le jeton, ou `null` si le stockage refuse de le rendre. */
function readToken(): string | null {
  try {
    return getToken();
  } catch {
    return null;
  }
}

function silently(run: () => void): void {
  try {
    run();
  } catch {
    // Silencieux par construction : voir la doc du module.
  }
}

export function enqueue(event: VirtuosaEvent): void {
  silently(() => {
    if (typeof window === "undefined" || isOptedOut()) {
      return;
    }

    const properties = event.properties as Record<string, unknown>;
    const occurredAt = Date.now();

    rollSessionIfStale(occurredAt);

    queue.push({
      name: event.name,
      occurredAt,
      pathname: pathnameOf(properties),
      properties,
    });

    if (queue.length > MAX_QUEUE) {
      queue = queue.slice(-MAX_QUEUE);
    }

    if (queue.length >= FLUSH_THRESHOLD) {
      flush();

      return;
    }

    scheduleFlush();
  });
}

function scheduleFlush(): void {
  if (timer !== null) {
    return;
  }

  timer = setTimeout(() => {
    timer = null;
    flush();
  }, FLUSH_INTERVAL_MS);
}

/**
 * Vide la file. La file est purgée **avant** l'envoi : un échec réseau ne doit
 * pas faire renvoyer les mêmes événements en boucle au flush suivant. On perd
 * un lot plutôt que de risquer une amplification.
 */
export function flush(): void {
  silently(sendQueue);
}

function sendQueue(): void {
  if (timer !== null) {
    clearTimeout(timer);
    timer = null;
  }

  if (queue.length === 0) {
    return;
  }

  const events = queue;
  queue = [];

  const body = JSON.stringify({ context: currentContext(), events });
  const headers: Record<string, string> = { "Content-Type": "application/json" };
  // Le jeton vit dans `localStorage` : sa lecture peut lever là où le stockage
  // est refusé. Un lot attribué à l'empreinte anonyme vaut mieux qu'un lot
  // perdu, et surtout mieux qu'une exception au milieu d'une déconnexion.
  const token = readToken();

  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  try {
    // `keepalive` laisse la requête aboutir après la fermeture de l'onglet,
    // tout en autorisant les en-têtes — contrairement à `sendBeacon`.
    void fetch(ENDPOINT, {
      method: "POST",
      headers,
      body,
      keepalive: true,
    }).catch(() => {
      // Silencieux par construction : voir la doc du module.
    });
  } catch {
    // Dernier recours pour les navigateurs sans `keepalive` : l'événement
    // partira sans en-tête d'authentification, donc attribué à l'empreinte
    // anonyme plutôt qu'au compte. Dégradé, mais pas perdu.
    if (typeof navigator !== "undefined" && typeof navigator.sendBeacon === "function") {
      navigator.sendBeacon(ENDPOINT, new Blob([body], { type: "application/json" }));
    }
  }
}
